// @flow
import * as React from 'react';
// eslint-disable-next-line import/named
import type { IntlShape } from 'react-intl';

// Components
import AppointmentFlowWizard from 'components/templates/AppointmentFlowWizard/';
import AppointmentFlowFooter from 'components/molecules/AppointmentFlowFooter/';
import AppointmentFlowHeader from 'components/molecules/AppointmentFlowHeader/';
import PanelDropOff from 'components/molecules/PanelDropOff';
import FatalError from 'components/api/components/FatalError/FatalError';

// CTA Components
import CTAFlowHeader from 'components/molecules/CTAFlowHeader';
import CtaStepIndicator from 'components/atoms/CtaStepIndicator';

// Utils
import {
  SERVICES_ROUTE,
  APPOINTMENT_ROUTE,
  TRANSPORTATION_ROUTE,
  INDIVIDUAL_SERVICES_ROUTE
} from 'constants/routes';
import { NAVIGATION } from 'constants/navigations';
import { labelsStepIndicator } from 'constants/CTAConfig';

import './DropOffPage.scss';

// Types
import { gtmUserEventType } from 'types/gtm';
import type { CTA } from 'api/xws/optimizely/optimizely';

const { FORWARD } = NAVIGATION;

export type Props = {
  goTo: (path: string, direction: string) => void,
  transportationOption: string,
  setTransportationInfo: (transportation: Object) => void,
  transportations: Array<Object>,
  isValetAvailable: Boolean,
  noAdvisorsError: Boolean,
  previousPath: String,
  currentPath: String,
  intl: IntlShape,
  ctaBanner: CTA | null,
  gtmUserEvent: gtmUserEventType,
  showProgressBar: boolean,
  hideBottomCTA: boolean,
  redirectedTo: boolean,
  sendTransportationOptionsShown: () => void,
  sendTransportationOptionsFinish: () => void
};

class DropOffPage extends React.Component<Props> {
  constructor(props: Props) {
    super(props);
    (this: any).isFooterBtnEnabled = this.isFooterBtnEnabled.bind(this);
    (this: any).onDropOffPageRoute = this.onDropOffPageRoute.bind(this);
    (this: any).noAdvisorsErrorPopup = React.createRef();
  }

  componentDidMount() {
    const {
      noAdvisorsError,
      previousPath,
      isValetAvailable,
      redirectedTo,
      sendTransportationOptionsShown
    } = this.props;
    const validPath = isValetAvailable ? SERVICES_ROUTE : TRANSPORTATION_ROUTE;
    const isAdvisorError =
      noAdvisorsError &&
      (validPath === previousPath ||
        INDIVIDUAL_SERVICES_ROUTE === previousPath);
    if (isAdvisorError) {
      (this: any).noAdvisorsErrorPopup.current.setShowModal(true);
    }

    if (redirectedTo) {
      sendTransportationOptionsShown();
    }
  }

  isFooterBtnEnabled() {
    const { transportationOption, transportations } = this.props;
    if (transportationOption) {
      return transportations.find(({ data }) => data === transportationOption);
    }
    return false;
  }

  onDropOffPageRoute() {
    const {
      transportations,
      transportationOption,
      setTransportationInfo,
      sendTransportationOptionsFinish
    } = this.props;
    sendTransportationOptionsFinish();
    const selectedTransportation = transportations.find(
      ({ data }) => data === transportationOption
    );
    if (selectedTransportation) {
      setTransportationInfo(selectedTransportation);
    }
  }

  render() {
    const {
      goTo,
      isValetAvailable,
      intl,
      ctaBanner,
      gtmUserEvent,
      showProgressBar,
      hideBottomCTA
    } = this.props;
    const onBackButton = isValetAvailable
      ? TRANSPORTATION_ROUTE
      : SERVICES_ROUTE;
    const backButtonAriaLabel = intl.formatMessage({
      id: 'c8.aria.back.to.services',
      defaultMessage: 'Back to services'
    });
    const nextButtonAriaLabel = intl.formatMessage({
      id: 'c8.aria.continue.to.select.appointment.date.and.time',
      defaultMessage: 'Continue to select appointment date and time'
    });
    const label = intl.formatMessage({
      id: isValetAvailable ? 'c8.header.transportation' : 'c8.header.services'
    });
    return (
      <React.Fragment>
        <FatalError
          key="fatalError"
          ref={(this: any).noAdvisorsErrorPopup}
          noAdvisorsError={true}
        />
        <AppointmentFlowWizard
          hideFooter={Boolean(hideBottomCTA)}
          header={() => {
            return ctaBanner !== null ? (
              <CTAFlowHeader
                label={label}
                hideServices={false}
                routeTo={onBackButton}
                backButtonAriaLabel={backButtonAriaLabel}
                nextButtonAriaLabel={nextButtonAriaLabel}
                ctaButtonDisabled={!this.isFooterBtnEnabled()}
                ctaBanner={ctaBanner}
                onClickCtaButton={() => {
                  this.onDropOffPageRoute();
                  gtmUserEvent(
                    'NEXT button',
                    'Clicked',
                    `Go to next page through CTA`,
                    `GTM_CURRENT_PAGE > ${APPOINTMENT_ROUTE} page`
                  );
                  goTo(APPOINTMENT_ROUTE, FORWARD);
                }}
                onClick={this.onDropOffPageRoute}
                typeButton={'cta-next'}
              />
            ) : (
              <AppointmentFlowHeader
                label={label}
                routeTo={onBackButton}
                onClick={this.onDropOffPageRoute}
                backButtonAriaLabel={backButtonAriaLabel}
              />
            );
          }}
          footer={() => {
            return !hideBottomCTA ? (
              <AppointmentFlowFooter
                buttonText={intl.formatMessage({ id: 'c8.footer.next' })}
                buttonDisabled={!this.isFooterBtnEnabled()}
                onClick={() => {
                  this.onDropOffPageRoute();
                  goTo(APPOINTMENT_ROUTE, FORWARD);
                }}
                ariaLabel={nextButtonAriaLabel}
              />
            ) : null;
          }}
          body={() => (
            <React.Fragment>
              {showProgressBar && (
                <CtaStepIndicator
                  htmlId="VehiclePageStepIndicator"
                  labels={labelsStepIndicator(intl)}
                  activeStep={3}
                />
              )}
              <PanelDropOff />
            </React.Fragment>
          )}
        />
      </React.Fragment>
    );
  }
}

export default DropOffPage;



// WEBPACK FOOTER //
// ./src/components/pages/DropOffPage/DropOffPage.js